import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import logo from '../assets/logo.png';
import { contactAPI } from '../services/api';

const ContactPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    location: '',
    subject: '',
    message: '',
    website: '', // honeypot — leave empty
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await contactAPI.submit(formData);
      setSubmitted(true);
    } catch (err) {
      let message;
      if (err.message === 'Too many requests. Please wait.') {
        message = "You've sent several messages recently. Please try again in a little while.";
      } else if (err.fieldErrors && typeof err.fieldErrors === 'object') {
        // DRF returns { fieldName: ["error message"] } — pick the first one
        const firstField = Object.keys(err.fieldErrors)[0];
        const firstError = err.fieldErrors[firstField];
        const errText = Array.isArray(firstError) ? firstError[0] : firstError;
        message = errText || 'Please check the form and try again.';
      } else {
        message = 'Something went wrong sending your message. Please try again, or email us directly.';
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead page="contact" />

      <div className="min-h-screen bg-[#05070d] text-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        {/* Decorative glow */}
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* Navigation */}
        <nav
          className={`fixed top-0 w-full z-50 transition-all duration-300 ${
            scrolled
              ? 'bg-[#05070d]/95 backdrop-blur-md border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)]'
              : 'bg-[#05070d] border-b border-white/10'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex items-center gap-3">
                <img
                  src={logo}
                  alt="RAL Real Advisor"
                  className="w-10 h-10 object-contain"
                />
                <div className="flex items-center">
                  <span className="text-2xl font-serif font-bold text-white">
                    RAL
                  </span>
                  <span className="text-2xl font-serif font-light text-[#9fc6ff] ml-1">
                    Real Advisor
                  </span>
                </div>
              </Link>

              <div className="flex items-center gap-6">
                <Link
                  to="/login"
                  className="text-sm text-white/80 hover:text-[#9fc6ff] uppercase tracking-wide"
                >
                  Sign In
                </Link>

                <Link
                  to="/contact"
                  className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="bg-[#05070d] pt-32 pb-24 relative">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl">
              <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-6 block">
                Contact Us
              </span>
              <h1 className="text-5xl lg:text-6xl font-serif font-bold text-white leading-tight mb-6">
                Let&apos;s Talk About Your Commercial Strategy
              </h1>
              <p className="text-xl text-neutral-400 leading-relaxed">
                Book a conversation, ask a question, or get in touch to discuss
                how RAL Real Advisor can support your business.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-24 lg:py-32 bg-neutral-100 text-neutral-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-3 gap-16">
              {/* Info */}
              <div className="lg:col-span-1">
                <h2 className="text-3xl font-serif font-bold mb-8">
                  Get in Touch
                </h2>

                <div className="space-y-8">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-[#05070d] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#9fc6ff]">✉️</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900 mb-2">Email</h3>
                      <p className="text-neutral-600">
                        <a
                          href="mailto:george@ralrealadvisor.cy"
                          className="hover:text-[#7eaee8] transition-colors"
                        >
                          george@ralrealadvisor.cy
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-[#05070d] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#9fc6ff]">📞</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900 mb-2">Phone</h3>
                      <p className="text-neutral-600">
                        <a
                          href="tel:+35799277774"
                          className="hover:text-[#7eaee8] transition-colors"
                        >
                          +357 99277774
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-[#05070d] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#9fc6ff]">🌍</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900 mb-2">Markets</h3>
                      <p className="text-neutral-600 leading-relaxed">
                        Supporting real estate developers across multiple markets.
                      </p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Form */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-neutral-200 p-10 lg:p-12">
                  {submitted ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 bg-[#7eaee8] flex items-center justify-center mx-auto mb-8">
                        <svg
                          className="w-10 h-10 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <h3 className="text-3xl font-serif font-bold text-neutral-900 mb-4">
                        Thank You
                      </h3>
                      <p className="text-neutral-600 text-lg mb-8">
                        Your message has been received. We&apos;ll get back to you soon.
                      </p>
                      <button
                        onClick={() => {
                          setSubmitted(false);
                          setError(null);
                          setFormData({
                            name: '',
                            email: '',
                            company: '',
                            phone: '',
                            location: '',
                            subject: '',
                            message: '',
                            website: '',
                          });
                        }}
                        className="text-[#7eaee8] font-medium hover:text-[#5c97de] transition-colors"
                      >
                        Send another message →
                      </button>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-3xl font-serif font-bold text-neutral-900 mb-2">
                        Send a Message
                      </h2>
                      <p className="text-neutral-600 mb-8">
                        Tell us a little about your business and what you need.
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                              Full Name *
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent"
                              placeholder="Your name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                              Email *
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent"
                              placeholder="your@email.com"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                              Company
                            </label>
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent"
                              placeholder="Company name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                              Phone
                            </label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent"
                              placeholder="+123 456 789"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                              Market / Location
                            </label>
                            <input
                              type="text"
                              name="location"
                              value={formData.location}
                              onChange={handleChange}
                              className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent"
                              placeholder="Your market or region"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                              Subject *
                            </label>
                            <select
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent"
                            >
                              <option value="">Select a subject...</option>
                              <option value="consultation">Consultation</option>
                              <option value="diagnostic">Diagnostic Request</option>
                              <option value="advisory">Advisory Support</option>
                              <option value="partnership">Partnership</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-2 tracking-wide uppercase">
                            Message
                          </label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={6}
                            className="w-full px-4 py-4 bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#7eaee8] focus:border-transparent resize-none"
                            placeholder="Tell us how we can help..."
                          />
                        </div>

                        {/* Honeypot — invisible to users, bots will fill it */}
                        <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                          <label htmlFor="website">Website (do not fill in)</label>
                          <input
                            type="text"
                            id="website"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            tabIndex="-1"
                            autoComplete="off"
                          />
                        </div>

                        <div className="flex items-center gap-3 text-sm text-neutral-500">
                          <input type="checkbox" id="gdpr" required className="w-4 h-4 accent-[#7eaee8]" />
                          <label htmlFor="gdpr">
                            I agree to the{' '}
                            <Link
                              to="/privacy"
                              className="text-[#7eaee8] hover:underline"
                            >
                              Privacy Policy
                            </Link>{' '}
                            and consent to being contacted.
                          </label>
                        </div>

                        {error && (
                          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                            {error}
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full bg-[#05070d] text-white py-5 font-medium tracking-wide uppercase hover:bg-neutral-900 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {submitting ? 'Sending...' : 'Send Message'}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Contact Options */}
        <section className="py-24 bg-neutral-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-serif font-bold text-white mb-4">
                Prefer a Different Way to Connect?
              </h2>
              <p className="text-neutral-400">
                Choose the option that works best for you
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {[
                {
                  icon: '📞',
                  title: 'Call Directly',
                  desc: 'Speak directly to discuss your commercial goals.',
                  cta: 'Call Now',
                  link: 'tel:+35799277774',
                },
                {
                  icon: '✉️',
                  title: 'Email',
                  desc: 'Send an email if you prefer a written introduction.',
                  cta: 'Send Email',
                  link: 'mailto:george@ralrealadvisor.cy',
                },
              ].map((option, index) => (
                <div key={index} className="bg-neutral-800 p-8 text-center border border-white/10">
                  <div className="text-4xl mb-4">{option.icon}</div>
                  <h3 className="text-xl font-serif font-bold text-white mb-2">
                    {option.title}
                  </h3>
                  <p className="text-neutral-400 mb-6">{option.desc}</p>
                  <a
                    href={option.link}
                    className="inline-block border border-[#7eaee8] text-[#9fc6ff] px-6 py-3 font-medium hover:bg-[#7eaee8] hover:text-white transition-all"
                  >
                    {option.cta}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-neutral-950 pt-20 pb-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-4 gap-12 pb-16 border-b border-neutral-800">
              <div className="lg:col-span-2">
                <Link to="/" className="flex items-center gap-3 mb-6">
                  <img
                    src={logo}
                    alt="RAL Real Advisor"
                    className="w-10 h-10 object-contain"
                  />
                  <div className="flex items-center">
                    <span className="text-2xl font-serif font-bold text-white">
                      RAL
                    </span>
                    <span className="text-2xl font-serif font-light text-[#9fc6ff] ml-1">
                      Real Advisor
                    </span>
                  </div>
                </Link>
                <p className="text-neutral-500 leading-relaxed mb-6 max-w-md">
                  Commercial optimisation, GTM execution, and pricing strategy
                  for real estate developers and commercial teams.
                </p>
                <div className="flex items-center gap-2 text-neutral-400 text-sm">
                  <span>🌍</span>
                  <span>Supporting real estate developers across multiple markets</span>
                </div>
              </div>

              <div>
                <h4 className="text-white font-medium mb-6 tracking-wide uppercase text-sm">
                  Quick Links
                </h4>
                <ul className="space-y-4">
                  {[
                    { name: 'Home', to: '/' },
                    { name: 'Diagnostic Tool', to: '/diagnostic' },
                    { name: 'Contact', to: '/contact' },
                  ].map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.to}
                        className="text-neutral-500 hover:text-[#9fc6ff] transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-white font-medium mb-6 tracking-wide uppercase text-sm">
                  Contact
                </h4>
                <ul className="space-y-4 text-neutral-500">
                  <li>
                    <a
                      href="mailto:george@ralrealadvisor.cy"
                      className="hover:text-[#9fc6ff] transition-colors"
                    >
                      george@ralrealadvisor.cy
                    </a>
                  </li>
                  <li>
                    <a
                      href="tel:+35799277774"
                      className="hover:text-[#9fc6ff] transition-colors"
                    >
                      +357 99277774
                    </a>
                  </li>
                  <li>RAL Real Advisor Ltd</li>
                </ul>
              </div>
            </div>

            <div className="pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-neutral-600">
              <p>© {new Date().getFullYear()} RAL Real Advisor Ltd. All rights reserved.</p>
              <div className="flex gap-6 mt-4 md:mt-0">
                <Link
                  to="/privacy"
                  className="hover:text-[#9fc6ff] transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="hover:text-[#9fc6ff] transition-colors"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactPage;